import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { get } from 'lodash'
import { useEffect, useState } from 'react'
import {
  Button as RaButton,
  DateInput,
  FileField,
  Labeled,
  Loading,
  required,
  SelectInput,
  TextField,
  TextInput,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { InlineSpinner } from 'shared/components/Spinner'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { toSnakeCase } from 'shared/helpers/utils'
import {
  ChildrenDocument,
  ChildrenDocumentAttachment,
  ChildrenDocumentMissing,
  decryptDocument,
} from 'shared/models/document'

import { BaseUploadButton } from 'components/Common/UploadButton'
import { base64toBlob, convertFileToBase64 } from 'helpers/utils'
import { getDocuments } from '../../api/core'
import {
  DOCUMENT_ACTIONS,
  DOCUMENT_STATUS,
  DOCUMENTS_WITH_EFFECTIVE_DATE,
  DOCUMENTS_WITH_EXPIRATION_DATE_OPTIONAL,
  DRIVER_INFO,
  DRIVER_LICENSE,
  DRIVER_LICENSE_BACK,
  DRIVER_LICENSE_FRONT,
  DRIVER_LICENSE_ISSUE_DATE,
  DRIVER_LICENSE_NUMBER,
  DRIVER_LICENSE_STATE,
  ENCRYPTED_DOCUMENTS,
  EQUIPMENT_FOLDERS,
  FOLDER_NAME_TO_CATEGORY,
  getAttachmentFileName,
  getDocumentActions,
  getFileAttachmentOnDocument,
  getLicenseBack,
  getLicenseFront,
  isLastDOTInspection,
  PERMITBOOK_DOCUMENTS,
  REVIEW_NEEDED,
  TEAM,
  TEAM_CT_LEASE_AGREEMENT,
  TRUCK,
  VALID,
} from '../../helpers/documents'
import { getStateChoices, US_STATES } from '../../helpers/states'
import { useUser } from '../../helpers/useUser'
import { futureDateValidator, validateFutureDate, validatePastDate } from '../../helpers/validators'
import { hasPermission, Permissions } from '../../Permissions'
import { CustomDateField, SimpleCalloutField } from '../Fields'

const useStyles = makeStyles({
  row: {
    '&:hover': {
      '& .uploadButton': {
        visibility: 'visible',
      },
      cursor: 'pointer',
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  attachmentRow: {
    width: '90%',
  },
  documentFileNameTableCell: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0',
    border: 'none',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    marginLeft: '20%',
    height: '65px',
  },
  tableHeader: {
    fontWeight: 'bold',
  },
  attachmentTableHeader: {
    borderBottom: 'none',
    fontWeight: 'normal',
  },
  uploadButton: {
    textTransform: 'none',
    visibility: 'hidden',
  },
  documentNameTableCell: {
    width: '30%',
  },
  statusTableCell: {
    width: '10%',
    height: '65px',
    padding: '17px 16px 16px 16px',
  },
  dateExpiredTableCell: {
    width: '13%',
  },
  categoryTableCell: {
    width: '15%',
  },
  tableCell: {
    maxWidth: '10%',
  },
  documentNameWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  documentNameAndDecryptButton: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  documentDecryptButton: {
    textTransform: 'none',
  },
  documentIcon: {
    marginRight: '10px',
  },
  fullWidth: {
    width: '100%',
  },
  documentActionsMenu: {
    '& ul': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  actionsMenuButton: {
    width: '100%',
    justifyContent: 'flex-start',
  },
})

const getUploadInputs = (
  document: any,
  attachments: any,
  isUpdate: boolean,
  isApproved: boolean,
  setIsApproved: any
) => {
  const metadata = attachments && attachments.length > 0 ? attachments[0].metadata : document.metadata
  const isApprovedFormObj = {
    name: 'is_approved',
    input: Checkbox,
    value: isApproved,
    onChange: setIsApproved,
    label: 'Mark as approved',
    inputProps: { value: isApproved },
    isTop: false,
    size: 12,
  }
  if (isLastDOTInspection(document.document_type)) {
    return [
      {
        name: 'date_last_dot_inspection',
        input: DateInput,
        validate: validatePastDate,
        label: 'Date Last DOT Inspection',
        value: isUpdate ? metadata?.LAST_DOT_INSPECTION_DATE : null,
      },
      isApprovedFormObj,
    ]
  }
  let formDataInputs: any = [
    {
      name: 'date_expired',
      input: DateInput,
      choices: undefined,
      validate: DOCUMENTS_WITH_EXPIRATION_DATE_OPTIONAL.includes(document.document_type)
        ? [futureDateValidator]
        : validateFutureDate,
      value: isUpdate ? metadata?.date_expired : null,
      label: 'Date Expired',
      isTop: document.document_type === DRIVER_LICENSE,
    },
    isApprovedFormObj,
  ]

  if (document.document_type === TEAM_CT_LEASE_AGREEMENT) {
    formDataInputs = []
  }

  if (document.document_type === DRIVER_LICENSE) {
    formDataInputs.unshift(
      {
        name: 'license_number',
        input: TextInput,
        value: isUpdate ? metadata?.DRIVER_LICENSE_NUMBER : null,
        validate: required(),
        label: 'License Number',
        isTop: true,
      },
      {
        name: 'license_state',
        input: SelectInput,
        choices: getStateChoices(US_STATES),
        value: isUpdate ? metadata?.DRIVER_LICENSE_STATE : null,
        validate: required(),
        label: 'License State',
        isTop: true,
      },
      {
        name: 'license_issue_date',
        input: DateInput,
        value: isUpdate ? metadata?.DRIVER_LICENSE_ISSUE_DATE : null,
        validate: validatePastDate,
        label: 'Issue Date',
        isTop: true,
      }
    )
  }

  if (DOCUMENTS_WITH_EFFECTIVE_DATE.includes(document.document_type)) {
    let defaultValue =
      document.document_type === TEAM_CT_LEASE_AGREEMENT ? new Date().toLocaleDateString('en-CA') : null
    formDataInputs.unshift({
      name: 'date_effective',
      input: DateInput,
      value: isUpdate ? metadata?.date_effective : defaultValue,
      label: 'Effective Date',
      isTop: document.document_type === DRIVER_LICENSE,
      choices: undefined,
      validate: [],
    })
  }

  return formDataInputs
}

const getLicenseFileInputs = (attachments: any, isUpdate: any) => {
  return [
    {
      name: DRIVER_LICENSE_FRONT,
      label: 'License Front',
      value: isUpdate ? getFileAttachmentOnDocument(getLicenseFront(attachments)) : null,
    },
    {
      name: DRIVER_LICENSE_BACK,
      label: 'License Back',
      value: isUpdate ? getFileAttachmentOnDocument(getLicenseBack(attachments)) : null,
    },
  ]
}

const getAttachmentFileInputs = (document: any, attachment: any, isUpdate: boolean) => {
  return [
    {
      name: getAttachmentFileName(document),
      label: '',
      value: isUpdate ? getFileAttachmentOnDocument(attachment) : null,
    },
  ]
}

interface EncryptedFile {
  attachment_file_encrypted: string
  original_filename: string
}

const encryptFile = async (file: any) => {
  return convertFileToBase64(file).then(
    (convertedFileToBase64): EncryptedFile => ({
      attachment_file_encrypted: convertedFileToBase64,
      original_filename: file.name,
    })
  )
}

const LicenseDetailModal = ({ attachments, showDialog, handleClose }: any) => {
  const { metadata }: any = attachments ? attachments[0] : {}
  const licenseFront = getLicenseFront(attachments)
  const licenseBack = getLicenseBack(attachments)

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dialog fullWidth open={showDialog} onClose={handleClose} aria-label="Upload Document Modal">
        <Grid container justifyContent="space-between">
          <DialogTitle>CDL</DialogTitle>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
        <DialogContent>
          <Grid container spacing={4} direction="column" justifyContent="center" alignItems="center">
            <Grid item xs={4} container direction="row" justifyContent="flex-start">
              <Labeled label="License Number">
                <TextField record={metadata} source={DRIVER_LICENSE_NUMBER} />
              </Labeled>
            </Grid>
            <Grid item xs={4} container direction="row" justifyContent="flex-start">
              <Labeled label="License State">
                <TextField record={metadata} source={DRIVER_LICENSE_STATE} />
              </Labeled>
            </Grid>
            <Grid item xs={4} container direction="row" justifyContent="flex-start">
              <Labeled label="Issued Date">
                <TextField record={metadata} source={DRIVER_LICENSE_ISSUE_DATE} />
              </Labeled>
            </Grid>
            <Grid item xs={4} container direction="row" justifyContent="flex-start">
              <Labeled label="Expiration Date">
                <TextField record={metadata} source="date_expired" />
              </Labeled>
            </Grid>
            <Grid item xs={4} container direction="row" justifyContent="flex-start">
              <Labeled label="License Front">
                <FileField
                  target="_blank"
                  record={licenseFront}
                  source="attachment_file"
                  title="View Front"
                />
              </Labeled>
            </Grid>
            <Grid item xs={4} container direction="row" justifyContent="flex-start">
              <Labeled label="License Back">
                <FileField target="_blank" record={licenseBack} source="attachment_file" title="View Back" />
              </Labeled>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const USER_PAGE_DISPLAY_FOLDERS = [DRIVER_INFO, TRUCK]
const TEAM_FOLDERS = [TEAM]

const DocumentStatus = (props: any) => {
  const { item, styles } = props
  return (
    <TableCell className={styles.statusTableCell}>
      {item?.status === VALID ? (
        <img src={DOCUMENT_STATUS[item?.status].icon} width="24px" alt="" />
      ) : (
        <SimpleCalloutField
          styleProps={{
            style: {
              color: DOCUMENT_STATUS[item?.status].color,
              backgroundColor: DOCUMENT_STATUS[item?.status].backgroundColor,
              fontSize: 'medium',
              borderRadius: '3px',
            },
          }}
          label={DOCUMENT_STATUS[item?.status].value}
          labelVariant="body2"
        />
      )}
    </TableCell>
  )
}

const UploadButton = (props: any) => {
  const {
    document,
    attachments,
    uploadDocument,
    record,
    label,
    buttonClassNames,
    isUpdate,
    onDocumentUpload,
  } = props
  const [isApproved, setIsApproved] = useState(isUpdate ? document?.status === VALID : false)

  const documentUpload = async (id: any, formData: any) => {
    const documentType = formData.get('document_type')

    if (documentType === DRIVER_LICENSE) {
      const metadata = {
        DRIVER_LICENSE_ISSUE_DATE: formData.get('license_issue_date'),
        DRIVER_LICENSE_NUMBER: formData.get('license_number'),
        DRIVER_LICENSE_STATE: formData.get('license_state'),
      }

      formData.append('metadata', JSON.stringify(metadata))
    }

    if (isLastDOTInspection(documentType)) {
      const metadata = {
        LAST_DOT_INSPECTION_DATE: formData.get('date_last_dot_inspection'),
      }
      formData.append('metadata', JSON.stringify(metadata))
    }
    formData.append('review_status', isApproved ? VALID : REVIEW_NEEDED)

    if (ENCRYPTED_DOCUMENTS.includes(documentType)) {
      const attachment_name = getAttachmentFileName(document)
      const file = formData.get(attachment_name)

      if (file) {
        const encryptedFile: EncryptedFile = await encryptFile(file)
        formData.delete(attachment_name)

        formData.append(attachment_name, get(encryptedFile, 'attachment_file_encrypted'))
        formData.append(
          `${documentType?.toLowerCase()}_original_filename`,
          get(encryptedFile, 'original_filename')
        )
      }
    }
    const documentId = isUpdate ? id : ''

    const upload = await uploadDocument(documentId, formData, ENCRYPTED_DOCUMENTS.includes(documentType))
    await onDocumentUpload?.()
    return upload
  }

  return (
    <BaseUploadButton
      record={document}
      uploadFunction={documentUpload}
      fileFieldInputs={
        document.document_type === DRIVER_LICENSE
          ? getLicenseFileInputs(attachments, isUpdate)
          : getAttachmentFileInputs(document, attachments ? attachments[0] : undefined, isUpdate)
      }
      dialogTitle={label + ' ' + document.document_name}
      buttonLabel={label}
      formDataInputs={getUploadInputs(document, attachments, isUpdate, isApproved, setIsApproved)}
      additionalPayloadData={{
        folder_name: document.folder_name,
        document_type: document.document_type,
        related_object_pk: record?.id,
        is_update: !!document.id,
      }}
      buttonVariant="text"
      buttonClassNames={buttonClassNames}
      multiple={false}
    />
  )
}

const ReviewButton = (props: any) => {
  const { document, styles, action, onDocumentUpload } = props
  const notify = useNotify()
  const refresh = useRefresh()

  const handleClick = (event: any) => {
    event.preventDefault()
    event.stopPropagation()

    action
      .api(document.id, action.payload)
      .then(() => {
        notify('Document reviewed!', { type: 'success' })
        onDocumentUpload?.()
        refresh()
        props.refetch?.()
      })
      .catch((error: any) => {
        notify(`Oops! Something went wrong: ${JSON.stringify(error?.response?.data)}`, { type: 'error' })
        refresh()
        props.refetch?.()
      })
  }

  return (
    <RaButton
      type="button"
      variant="text"
      label={action.label}
      onClick={handleClick}
      className={styles.actionsMenuButton}
    />
  )
}

const DocumentActionMenu = (props: any) => {
  const { document, attachments, documentActions, record, styles, onDocumentUpload } = props
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: any) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (event: any) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  return (
    <>
      <RaButton
        type="button"
        variant="text"
        label="Actions"
        className={styles.uploadButton + ' uploadButton'}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
      />
      <Menu
        id="document-actions"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={styles.documentActionsMenu}
      >
        {documentActions.map((action: any) => {
          return [DOCUMENT_ACTIONS.CREATE.id, DOCUMENT_ACTIONS.UPDATE.id].includes(action.id) ? (
            <UploadButton
              document={document}
              attachments={attachments}
              record={record}
              uploadDocument={action.api}
              label={action.label}
              styles={styles}
              isUpdate={DOCUMENT_ACTIONS.UPDATE.id === action.id}
              buttonClassNames={styles.actionsMenuButton}
              onDocumentUpload={onDocumentUpload}
              key={`${document.id}-${action.id}`}
            />
          ) : (
            <ReviewButton
              styles={styles}
              action={action}
              document={document}
              onDocumentUpload={onDocumentUpload}
              key={`${document.id}-${action.id}`}
              refetch={props.refetch}
            />
          )
        })}
      </Menu>
    </>
  )
}

const DocumentActions = (props: any) => {
  const {
    document,
    attachments,
    styles,
    canUploadDocument,
    isEquipmentPage,
    record,
    handleEquipmentUpdateClick,
    onDocumentUpload,
  } = props

  const documentActions = getDocumentActions(document, attachments ? attachments[0] : undefined)

  if (!canUploadDocument) {
    return null
  }

  return EQUIPMENT_FOLDERS.includes(document.folder_name) && !isEquipmentPage ? (
    <RaButton
      type="button"
      variant="text"
      label="Update on fleet page"
      onClick={handleEquipmentUpdateClick}
      className={styles.uploadButton + ' uploadButton'}
    />
  ) : documentActions.length > 1 ? (
    <DocumentActionMenu
      document={document}
      attachments={attachments}
      documentActions={documentActions}
      record={record}
      styles={styles}
      onDocumentUpload={onDocumentUpload}
      refetch={props.refetch}
    />
  ) : (
    ([DRIVER_INFO, TEAM].includes(document.folder_name) || isEquipmentPage) &&
    documentActions.length === 1 && (
      <UploadButton
        document={document}
        attachments={attachments}
        record={record}
        uploadDocument={documentActions[0].api}
        label={documentActions[0].label}
        styles={styles}
        isUpdate={documentActions[0].id === DOCUMENT_ACTIONS.UPDATE.id}
        buttonClassNames={styles.uploadButton + ' uploadButton'}
        onDocumentUpload={onDocumentUpload}
      />
    )
  )
}

const DocumentAttachmentVersionRow = (props: any) => {
  const {
    document,
    attachments,
    styles,
    handleRowClick,
    isEquipmentPage,
    renderDocumentActions,
    onDecryptDocument,
    isAttachmentDecrypted,
    label,
  } = props
  const effectiveDateSource = "metadata['date_effective']"
  const expiredDateSource = "metadata['date_expired']"
  const attachment = attachments[0]
  return (
    <TableRow
      key={attachment.id}
      className={styles.row}
      onClick={() => handleRowClick(document, attachments)}
    >
      <TableCell className={styles.documentFileNameTableCell}>
        <div className={styles.documentNameWrapper}>{label}</div>
        {attachment?.attachment_file_encrypted && !isAttachmentDecrypted(attachment) && (
          <RaButton
            type="button"
            variant="text"
            label="Reveal"
            className={styles.documentDecryptButton}
            onClick={() => onDecryptDocument(document, attachments)}
          />
        )}
      </TableCell>
      <DocumentStatus item={attachment} styles={styles} />
      <TableCell className={styles.dateExpiredTableCell}>
        {get(attachment, effectiveDateSource) ? (
          <CustomDateField record={attachment} source={effectiveDateSource} />
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className={styles.dateExpiredTableCell}>
        {get(attachment, expiredDateSource) ? (
          <CustomDateField record={attachment} source={expiredDateSource} />
        ) : (
          '-'
        )}
      </TableCell>
      {!isEquipmentPage && <TableCell className={styles.categoryTableCell} />}
      <TableCell className={styles.tableCell}>{renderDocumentActions(document, attachments)}</TableCell>
    </TableRow>
  )
}

const DocumentAttachmentRows = (props: any) => {
  const {
    document,
    record,
    styles,
    canUploadDocument,
    handleEquipmentUpdateClick,
    handleRowClick,
    isEquipmentPage,
    renderDocumentActions,
    onDecryptDocument,
    isAttachmentDecrypted,
  } = props

  return (
    <>
      <TableRow>
        <TableCell className={`${styles.documentNameTableCell} ${styles.attachmentTableHeader}`}>
          <div className={styles.documentNameWrapper}>
            <DescriptionIcon
              color={document.document_attachments ? 'primary' : 'disabled'}
              className={styles.documentIcon}
            />
            {document.document_name}
          </div>
        </TableCell>
        <TableCell className={`${styles.tableHeader} ${styles.attachmentTableHeader}`} />
        <TableCell className={`${styles.tableHeader} ${styles.attachmentTableHeader}`} />
        <TableCell className={`${styles.tableHeader} ${styles.attachmentTableHeader}`} />
        {!isEquipmentPage && (
          <TableCell className={`${styles.tableHeader} ${styles.attachmentTableHeader}`}>
            {FOLDER_NAME_TO_CATEGORY[document.folder_name]}
          </TableCell>
        )}
        <TableCell className={`${styles.tableHeader} ${styles.attachmentTableHeader}`} />
      </TableRow>
      {document.document_attachment_versions.map((attachments: any) => {
        return (
          <DocumentAttachmentVersionRow
            label={
              attachments[0].version_number === document.document_attachment_versions[0][0].version_number
                ? 'Previous upload'
                : 'Latest upload'
            }
            document={document}
            attachments={attachments}
            styles={styles}
            isEquipmentPage={isEquipmentPage}
            canUploadDocument={canUploadDocument}
            handleEquipmentUpdateClick={handleEquipmentUpdateClick}
            record={record}
            handleRowClick={handleRowClick}
            key={attachments ? attachments[0].id : document.id}
            renderDocumentActions={renderDocumentActions}
            onDecryptDocument={onDecryptDocument}
            isAttachmentDecrypted={isAttachmentDecrypted}
          />
        )
      })}
    </>
  )
}

const DocumentInfo = (props: any) => {
  const {
    document,
    styles,
    handleRowClick,
    isEquipmentPage,
    renderDocumentActions,
    onDecryptDocument,
    isAttachmentDecrypted,
  } = props

  const hasAttachments = document.document_attachment_versions?.length > 0
  const effectiveDateSource =
    document.document_type === DRIVER_LICENSE && !hasAttachments
      ? 'metadata["DRIVER_LICENSE_ISSUE_DATE"]'
      : 'metadata[date_effective]'
  const dateExpiredSource = hasAttachments ? "metadata['date_expired']" : 'date_expired'
  const latestAttachmentVersion = document.document_attachment_versions
    ? document.document_attachment_versions[0]
    : undefined
  const documentInfoObj = latestAttachmentVersion ? latestAttachmentVersion[0] : document

  return (
    <TableRow
      key={document.id}
      className={styles.row}
      onClick={() => handleRowClick(document, latestAttachmentVersion)}
    >
      <TableCell className={styles.documentNameTableCell}>
        <div className={styles.documentNameAndDecryptButton}>
          <div className={styles.documentNameWrapper}>
            <DescriptionIcon
              color={latestAttachmentVersion ? 'primary' : 'disabled'}
              className={styles.documentIcon}
            />
            {document.document_name}
          </div>
          {latestAttachmentVersion &&
            latestAttachmentVersion[0].attachment_file_encrypted &&
            !isAttachmentDecrypted(latestAttachmentVersion[0]) && (
              <RaButton
                type="button"
                variant="text"
                label="Reveal"
                className={styles.documentDecryptButton}
                onClick={() => onDecryptDocument(document, latestAttachmentVersion)}
              />
            )}
        </div>
      </TableCell>
      <DocumentStatus item={documentInfoObj} styles={styles} />
      <TableCell className={styles.dateExpiredTableCell}>
        {get(documentInfoObj, effectiveDateSource) ? (
          <CustomDateField record={documentInfoObj} source={effectiveDateSource} />
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className={styles.dateExpiredTableCell}>
        {hasAttachments || document.date_expired ? (
          <CustomDateField record={documentInfoObj} source={dateExpiredSource} />
        ) : (
          '-'
        )}
      </TableCell>
      {!isEquipmentPage && (
        <TableCell className={styles.categoryTableCell}>
          {FOLDER_NAME_TO_CATEGORY[document.folder_name]}
        </TableCell>
      )}
      <TableCell className={styles.tableCell} onClick={(e) => e.preventDefault()}>
        {renderDocumentActions(document, latestAttachmentVersion)}
      </TableCell>
    </TableRow>
  )
}

export const DocumentRow = (props: any) => {
  const {
    document,
    styles,
    canUploadDocument,
    handleEquipmentUpdateClick,
    isTeamPage,
    isEquipmentPage,
    record,
    permissions,
  } = props

  const foldersToDisplay = isEquipmentPage
    ? EQUIPMENT_FOLDERS
    : isTeamPage
    ? TEAM_FOLDERS
    : USER_PAGE_DISPLAY_FOLDERS

  return foldersToDisplay.includes(document?.folder_name) ? (
    <DocumentProvider
      permissions={permissions}
      record={record}
      userId={record.id}
      isTeamPage={isTeamPage}
      isEquipmentPage={isEquipmentPage}
    >
      {(onDocumentOpen, onDecryptDocument, renderDocumentActions, isAttachmentDecrypted) => (
        <>
          {document.document_attachment_versions?.length > 1 ? (
            <DocumentAttachmentRows
              document={document}
              record={record}
              styles={styles}
              canUploadDocument={canUploadDocument}
              handleEquipmentUpdateClick={handleEquipmentUpdateClick}
              handleRowClick={onDocumentOpen}
              isEquipmentPage={isEquipmentPage}
              renderDocumentActions={renderDocumentActions}
              onDecryptDocument={onDecryptDocument}
              isAttachmentDecrypted={isAttachmentDecrypted}
            />
          ) : (
            <DocumentInfo
              document={document}
              styles={styles}
              canUploadDocument={canUploadDocument}
              handleEquipmentUpdateClick={handleEquipmentUpdateClick}
              record={record}
              handleRowClick={onDocumentOpen}
              isEquipmentPage={isEquipmentPage}
              isTeamPage={isTeamPage}
              renderDocumentActions={renderDocumentActions}
              onDecryptDocument={onDecryptDocument}
              isAttachmentDecrypted={isAttachmentDecrypted}
            />
          )}
        </>
      )}
    </DocumentProvider>
  ) : null
}

const DocumentRows = (props: any) => {
  return (
    <>
      {props?.folder?.children_documents_present &&
        props?.folder.children_documents_present.map(
          (document: any, idx: any) =>
            PERMITBOOK_DOCUMENTS.includes(document.document_type) && (
              <DocumentRow key={`document-row-present-${idx}`} {...props} document={document} />
            )
        )}
      {props?.folder?.children_documents_missing &&
        props?.folder.children_documents_missing.map(
          (document: any, idx: any) =>
            PERMITBOOK_DOCUMENTS.includes(document.document_type) && (
              <DocumentRow key={`document-row-missing-${idx}`} {...props} document={document} />
            )
        )}
      {props?.folder?.children_folders &&
        props?.folder.children_folders.map((folder: any, idx: any) => (
          <DocumentRows key={`document-rows-${idx}`} {...props} folder={folder} />
        ))}
    </>
  )
}

const DocumentList = (props: any) => {
  const styles = useStyles()
  const redirect = useRedirect()
  const [folder, setFolder] = useState(null)
  const { record, folderName, permissions, isEquipmentPage, isTeamPage } = props
  const recordId = record?.id
  const canUploadDocument = hasPermission(permissions || [], Permissions.AddDocument)

  useEffect(() => {
    if (!recordId) return
    getDocuments(recordId, folderName).then((data) => {
      if (data) {
        setFolder(data)
      }
    })
  }, [recordId, folderName])

  const handleEquipmentUpdateClick = (e: any) => {
    e.stopPropagation()
    const teamId = record?.team?.id
    const redirectPath = `/teams/${teamId}/show/equipment/`
    redirect(redirectPath)
  }

  if (!folder) {
    return (
      <>
        <Box mt={5} />
        <Loading />
        <Box mt={5} />
      </>
    )
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={styles.tableHeader}>Document</TableCell>
              <TableCell className={styles.tableHeader}>Status</TableCell>
              <TableCell className={styles.tableHeader}>Effective Date</TableCell>
              <TableCell className={styles.tableHeader}>Expiration Date</TableCell>
              {!isEquipmentPage && <TableCell className={styles.tableHeader}>Category</TableCell>}
              <TableCell className={styles.tableHeader}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DocumentRows
              folder={folder}
              record={record}
              styles={styles}
              isEquipmentPage={isEquipmentPage}
              isTeamPage={isTeamPage}
              canUploadDocument={canUploadDocument}
              handleEquipmentUpdateClick={handleEquipmentUpdateClick}
              permissions={permissions}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default DocumentList

interface DocumentProviderProps {
  permissions: any
  record?: any
  userId: string
  isTeamPage: boolean
  isEquipmentPage: boolean
  children: (
    onDocumentOpen: (d: ChildrenDocument | ChildrenDocumentMissing, a?: ChildrenDocumentAttachment[]) => void,
    onDecryptDocument: (d: ChildrenDocument, a?: ChildrenDocumentAttachment[]) => void,
    renderActions: (
      d: ChildrenDocument | ChildrenDocumentMissing,
      a?: ChildrenDocumentAttachment[]
    ) => React.ReactNode,
    isAttachmentDecrypted: (a?: ChildrenDocumentAttachment) => boolean
  ) => React.ReactNode
}

export const DocumentProvider = ({
  children,
  permissions,
  record,
  userId,
  isTeamPage,
  isEquipmentPage,
}: DocumentProviderProps) => {
  const [licenseAttachments, setLicenseAttachments] = useState(undefined)
  const [showLicenseDialog, setShowLicenseDialog] = useState(false)
  const [documentsDecrypted, setDocumentsDecrypted] = useState<Record<string, string> | undefined>(undefined)

  const onDecryptDocument = (
    d: ChildrenDocument | ChildrenDocumentMissing,
    a?: ChildrenDocumentAttachment[]
  ) => {
    const document = toSnakeCase(d)
    const attachments = a?.map(toSnakeCase)

    if (attachments && attachments[0] && attachments[0].attachment_file_encrypted) {
      if ('id' in document && (!documentsDecrypted || !(attachments[0].id in documentsDecrypted))) {
        decryptDocument(document.id, attachments[0].id).then((data: any) => {
          if (documentsDecrypted) {
            setDocumentsDecrypted({
              ...documentsDecrypted,
              [attachments[0].id]: data.attachment_file_decrypted,
            })
          } else {
            setDocumentsDecrypted({ [attachments[0].id]: data.attachment_file_decrypted })
          }
        })
      }
    }
  }

  const onDocumentOpen = (
    d: ChildrenDocument | ChildrenDocumentMissing,
    a?: ChildrenDocumentAttachment[]
  ) => {
    const document = toSnakeCase(d)
    const attachments = a?.map(toSnakeCase)

    if (document.document_type === DRIVER_LICENSE) {
      setShowLicenseDialog(true)
      setLicenseAttachments(attachments as any)
    } else {
      if (
        attachments &&
        attachments[0].attachment_file_encrypted &&
        documentsDecrypted &&
        attachments[0].id in documentsDecrypted
      ) {
        const file = base64toBlob(documentsDecrypted[attachments[0].id])
        if (file) {
          window.open(URL.createObjectURL(file))
        }
        return
      }

      const file = attachments ? attachments[0].attachment_file : undefined

      if (file) {
        window.open(file)
      }
    }
  }

  const isAttachmentDecrypted = (a?: ChildrenDocumentAttachment | undefined) => {
    return Boolean(documentsDecrypted && a && a.id in documentsDecrypted)
  }

  const renderDocumentActions = (
    d: ChildrenDocument | ChildrenDocumentMissing,
    a?: ChildrenDocumentAttachment[],
    refetchDocuments?: () => Promise<any>
  ) => {
    return (
      <DocumentActionsWrap
        document={d}
        attachment={a}
        isTeamPage={isTeamPage}
        isEquipmentPage={isEquipmentPage}
        onDocumentUpload={refetchDocuments}
        permissions={permissions}
        record={record}
        userId={userId}
        children={children}
        licenseAttachments={licenseAttachments}
        setLicenseAttachments={setLicenseAttachments}
        showLicenseDialog={showLicenseDialog}
        setShowLicenseDialog={setShowLicenseDialog}
      />
    )
  }

  return <>{children(onDocumentOpen, onDecryptDocument, renderDocumentActions, isAttachmentDecrypted)}</>
}

function DocumentActionsWrap(
  props: DocumentProviderProps & {
    document: ChildrenDocument | ChildrenDocumentMissing
    attachment?: ChildrenDocumentAttachment[]
    licenseAttachments: any
    setLicenseAttachments: HandlerOf<any>
    showLicenseDialog: boolean
    setShowLicenseDialog: HandlerOf<boolean>
    onDocumentUpload?: () => Promise<any>
  }
) {
  const { user: userV1, isLoading, refetch } = useUser(props.userId, { enabled: !props.record })
  const user = props.record ? props.record : userV1
  const styles = useStyles()
  const redirect = useRedirect()
  const canUploadDocument = hasPermission(props.permissions || [], Permissions.AddDocument)
  const document = toSnakeCase(props.document)
  const attachments = props.attachment?.map(toSnakeCase)

  if (isLoading) return <InlineSpinner size="1.5em" />

  const handleLicenseDialogClose = (e: any) => {
    e.stopPropagation()
    props.setShowLicenseDialog(false)
    props.setLicenseAttachments(undefined)
  }

  const handleEquipmentUpdateClick = (e: any) => {
    e.stopPropagation()
    const teamId = user?.team?.id
    const redirectPath = `/teams/${teamId}/show/equipment/`
    redirect(redirectPath)
  }

  return (
    <>
      <DocumentActions
        document={document}
        attachments={attachments}
        record={user}
        styles={styles}
        isEquipmentPage={props.isEquipmentPage}
        isTeamPage={props.isTeamPage}
        canUploadDocument={canUploadDocument}
        handleEquipmentUpdateClick={handleEquipmentUpdateClick}
        onDocumentUpload={props.onDocumentUpload}
        refetch={!props.isEquipmentPage ? refetch : undefined}
      />
      {document.document_type === DRIVER_LICENSE && props.licenseAttachments && (
        <LicenseDetailModal
          document={document}
          user={user}
          handleClose={handleLicenseDialogClose}
          showDialog={props.showLicenseDialog}
          permissions={props.permissions}
          attachments={props.licenseAttachments}
        />
      )}
    </>
  )
}
